import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { EditorElement, AspectRatio, AdAnimation } from './types';
import { DEFAULT_TEXT_STYLE, DEFAULT_BUTTON_STYLE, DEFAULT_SUBTEXT_STYLE, DEFAULT_IMAGE_STYLE } from './types';
import EditorCanvas from './components/EditorCanvas';
import PropertiesPanel from './components/PropertiesPanel';
import EditorToolbar from './components/EditorToolbar';
import LayersPanel from './components/LayersPanel';
import EraseModal from '@/components/feature/EraseModal';
import { exportAnimatedVideo, exportStaticPng } from '@/lib/exportVideo';

export interface EditorLocationState {
  imageUrl: string;
  aspectRatio?: AspectRatio;
  initialHeadline?: string;
  initialSubheadline?: string;
  initialCta?: string;
}

const SAVE_KEY = 'adforge_editor_save';
const RECENT_PROJECTS_KEY = 'adforge_recent_projects';
const MAX_RECENT_PROJECTS = 5;
// Images are stored in separate keys to avoid localStorage quota limits with large base64 data URLs
const IMAGE_KEY_PREFIX = 'adforge_img_';
const MAIN_IMAGE_KEY = 'adforge_img_main';

interface SavedEditorState {
  elements: EditorElement[];
  overlayOpacity: number;
  animation: AdAnimation;
  imageFit: 'cover' | 'contain';
  imagePositionX: number;
  imagePositionY: number;
  savedAt: number;
  imageKey?: string;   // reference to separate image storage key (new format)
  imageUrl?: string;   // kept for backwards-compat reading only (old format)
  aspectRatio?: AspectRatio;
}

/** Save an image string to a dedicated localStorage key, separate from the main state */
function saveImageSeparately(key: string, imageUrl: string): void {
  try {
    localStorage.setItem(key, imageUrl);
  } catch {
    // Quota exceeded — silently skip; the image stays in memory
  }
}

/** Load an image from its dedicated key, with optional inline fallback (old format) */
function loadImageFromKey(imageKey?: string, inlineUrl?: string): string | undefined {
  if (imageKey) {
    const stored = localStorage.getItem(IMAGE_KEY_PREFIX + imageKey);
    if (stored) return stored;
  }
  // Fallback to inline URL from old saves
  return inlineUrl;
}

interface RecentProjectMeta {
  id: string;
  savedAt: number;
  thumbnail: string;
  elementCount: number;
  aspectRatio: AspectRatio;
}

async function createThumbnail(imageUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const MAX = 240;
      const scale = Math.min(MAX / img.width, MAX / img.height, 1);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(''); return; }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.65));
    };
    img.onerror = () => resolve('');
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
  });
}

function generateId() {
  return `el_${Math.random().toString(36).slice(2, 9)}`;
}

function buildInitialElements(
  headline?: string,
  subheadline?: string,
  cta?: string,
): EditorElement[] {
  const els: EditorElement[] = [];

  if (headline) {
    els.push({
      id: generateId(),
      type: 'text',
      x: 8,
      y: 60,
      content: headline,
      style: { ...DEFAULT_TEXT_STYLE },
      locked: false,
      visible: true,
    });
  }

  if (subheadline) {
    els.push({
      id: generateId(),
      type: 'text',
      x: 8,
      y: 74,
      content: subheadline,
      style: { ...DEFAULT_SUBTEXT_STYLE },
      locked: false,
      visible: true,
    });
  }

  if (cta) {
    els.push({
      id: generateId(),
      type: 'button',
      x: 8,
      y: 85,
      content: cta,
      style: { ...DEFAULT_BUTTON_STYLE },
      locked: false,
      visible: true,
    });
  }

  return els;
}

const MAX_HISTORY = 30;

export default function EditorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as EditorLocationState | null;

  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(state?.imageUrl ?? null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const aspectRatio: AspectRatio = state?.aspectRatio ?? '1:1';

  // Recent projects
  const sessionIdRef = useRef<string>(`session_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`);
  const [recentProjects, setRecentProjects] = useState<RecentProjectMeta[]>([]);
  const [currentThumbnail, setCurrentThumbnail] = useState<string>('');

  const [elements, setElements] = useState<EditorElement[]>(() =>
    buildInitialElements(state?.initialHeadline, state?.initialSubheadline, state?.initialCta)
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [overlayOpacity, setOverlayOpacity] = useState(0);
  const [animation, setAnimation] = useState<AdAnimation>('none');
  const [imageFit, setImageFit] = useState<'cover' | 'contain'>('cover');
  const [imagePositionX, setImagePositionX] = useState(50);
  const [imagePositionY, setImagePositionY] = useState(50);

  const [history, setHistory] = useState<EditorElement[][]>([]);
  const [future, setFuture] = useState<EditorElement[][]>([]);

  // Mobile panel state
  const [mobilePanelTab, setMobilePanelTab] = useState<'layers' | 'properties'>('layers');
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);

  // Stable ref — always reflects the latest elements without closure issues
  const elementsRef = useRef(elements);
  useEffect(() => { elementsRef.current = elements; }, [elements]);

  // Generate thumbnail whenever the background image changes
  useEffect(() => {
    if (!currentImageUrl) { setCurrentThumbnail(''); return; }
    createThumbnail(currentImageUrl).then(setCurrentThumbnail);
  }, [currentImageUrl]);

  // Load recent project metadata on mount + migrate legacy SAVE_KEY data
  useEffect(() => {
    try {
      const rawMeta = localStorage.getItem(RECENT_PROJECTS_KEY);
      let projects: RecentProjectMeta[] = rawMeta ? (JSON.parse(rawMeta) as RecentProjectMeta[]) : [];

      // ── Migrate existing SAVE_KEY save into Recent Projects ──────
      const rawSave = localStorage.getItem(SAVE_KEY);
      if (rawSave) {
        const saved: SavedEditorState = JSON.parse(rawSave);
        const MIGRATED_ID = 'migrated_legacy_save';
        // Resolve the image from old inline format or new key format
        const migratedImage = loadImageFromKey(saved.imageKey ? MAIN_IMAGE_KEY : undefined, saved.imageUrl);
        // Only migrate if it has an image and isn't already represented
        if (migratedImage && !projects.some((p) => p.id === MIGRATED_ID)) {
          const migratedMeta: RecentProjectMeta = {
            id: MIGRATED_ID,
            savedAt: saved.savedAt,
            thumbnail: '', // filled async below
            elementCount: saved.elements?.length ?? 0,
            aspectRatio: saved.aspectRatio ?? '1:1',
          };
          // Save image in its own dedicated key
          saveImageSeparately(IMAGE_KEY_PREFIX + MIGRATED_ID, migratedImage);
          // Persist state WITHOUT inlining the image
          const { imageUrl: _stripped, ...savedWithoutImage } = saved;
          const stateToMigrate: SavedEditorState = { ...savedWithoutImage, imageKey: MIGRATED_ID };
          localStorage.setItem(`adforge_project_${MIGRATED_ID}`, JSON.stringify(stateToMigrate));
          projects = [migratedMeta, ...projects];
          projects = projects.slice(0, MAX_RECENT_PROJECTS);
          localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(projects));

          // Generate thumbnail asynchronously then update the entry
          createThumbnail(migratedImage).then((thumb) => {
            if (!thumb) return;
            setRecentProjects((prev) => {
              const updated = prev.map((p) =>
                p.id === MIGRATED_ID ? { ...p, thumbnail: thumb } : p
              );
              localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(updated));
              return updated;
            });
          });
        }
      }

      setRecentProjects(projects);
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Export state
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveLabel, setSaveLabel] = useState<string>('Save');

  // HTML export state
  const [htmlCopied, setHtmlCopied] = useState(false);

  // Erase tool state
  const [eraseOpen, setEraseOpen] = useState(false);

  // Animated video export state
  const [isExportingVideo, setIsExportingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);

  // ── History helpers ────────────────────────────────────────────
  // Push current snapshot to history. Called ONCE at the START of a
  // drag or resize interaction — not on every pixel — so one undo
  // step = one complete move/resize action.
  const pushHistoryNow = useCallback(() => {
    const snap = elementsRef.current;
    setHistory((h) => [...h.slice(-MAX_HISTORY), snap]);
    setFuture([]);
  }, []);

  // Update multiple elements WITH history (add / delete / duplicate / etc.)
  const updateElements = useCallback(
    (updater: (prev: EditorElement[]) => EditorElement[]) => {
      setElements((prev) => {
        setHistory((h) => [...h.slice(-MAX_HISTORY), prev]);
        setFuture([]);
        return updater(prev);
      });
    },
    []
  );

  // Update a single element WITH history (Properties Panel changes, text edits)
  const updateElement = useCallback(
    (id: string, updates: Partial<EditorElement>) => {
      setElements((prev) => {
        setHistory((h) => [...h.slice(-MAX_HISTORY), prev]);
        setFuture([]);
        return prev.map((el) => (el.id === id ? { ...el, ...updates } : el));
      });
    },
    []
  );

  // Update a single element WITHOUT pushing history.
  // Used by EditorCanvas during live drag / resize — history was already
  // captured at interaction start via onInteractionStart={pushHistoryNow}.
  const updateElementSilent = useCallback(
    (id: string, updates: Partial<EditorElement>) => {
      setElements((prev) =>
        prev.map((el) => (el.id === id ? { ...el, ...updates } : el))
      );
    },
    []
  );

  // Move position — history captured at drag start, not on every pixel
  const updatePosition = useCallback(
    (id: string, x: number, y: number) => {
      setElements((prev) =>
        prev.map((el) => (el.id === id ? { ...el, x, y } : el))
      );
    },
    []
  );

  const handleImageFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (dataUrl) setCurrentImageUrl(dataUrl);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleImageChange = useCallback((file: File) => {
    handleImageFile(file);
  }, [handleImageFile]);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDraggingOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleImageFile(file);
    },
    [handleImageFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDraggingOver(false);
  }, []);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleImageFile(file);
      e.target.value = '';
    },
    [handleImageFile]
  );

  const addText = useCallback(() => {
    const newEl: EditorElement = {
      id: generateId(),
      type: 'text',
      x: 10,
      y: 20,
      content: 'New Text',
      style: { ...DEFAULT_TEXT_STYLE, fontSize: 18 },
      locked: false,
      visible: true,
    };
    updateElements((prev) => [...prev, newEl]);
    setSelectedId(newEl.id);
  }, [updateElements]);

  const addButton = useCallback(() => {
    const newEl: EditorElement = {
      id: generateId(),
      type: 'button',
      x: 10,
      y: 80,
      content: 'Click Here',
      style: { ...DEFAULT_BUTTON_STYLE },
      locked: false,
      visible: true,
    };
    updateElements((prev) => [...prev, newEl]);
    setSelectedId(newEl.id);
  }, [updateElements]);

  const addLogo = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (!dataUrl) return;
      const newEl: EditorElement = {
        id: generateId(), type: 'image', x: 10, y: 10,
        content: '', imageUrl: dataUrl,
        style: { ...DEFAULT_IMAGE_STYLE },
        locked: false, visible: true,
      };
      updateElements((prev) => [...prev, newEl]);
      setSelectedId(newEl.id);
    };
    reader.readAsDataURL(file);
  }, [updateElements]);

  const replaceImageElement = useCallback((id: string, file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (!dataUrl) return;
      updateElement(id, { imageUrl: dataUrl });
    };
    reader.readAsDataURL(file);
  }, [updateElement]);

  const addShape = useCallback(() => {
    const newEl: EditorElement = {
      id: generateId(),
      type: 'shape',
      x: 20,
      y: 20,
      content: '',
      style: {
        ...DEFAULT_BUTTON_STYLE,
        backgroundColor: 'rgba(0,0,0,0.4)',
        borderRadius: 8,
        width: 40,
        paddingX: 0,
        paddingY: 24,
        color: 'transparent',
      },
      locked: false,
      visible: true,
    };
    updateElements((prev) => [...prev, newEl]);
    setSelectedId(newEl.id);
  }, [updateElements]);

  const deleteElement = useCallback(
    (id: string) => {
      updateElements((prev) => prev.filter((el) => el.id !== id));
      setSelectedId(null);
    },
    [updateElements]
  );

  const duplicateElement = useCallback(
    (id: string) => {
      const el = elements.find((e) => e.id === id);
      if (!el) return;
      const newEl: EditorElement = {
        ...el,
        id: generateId(),
        x: Math.min(el.x + 3, 90),
        y: Math.min(el.y + 3, 90),
      };
      updateElements((prev) => [...prev, newEl]);
      setSelectedId(newEl.id);
    },
    [elements, updateElements]
  );

  const reorderElement = useCallback(
    (id: string, dir: 'up' | 'down') => {
      setElements((prev) => {
        const idx = prev.findIndex((e) => e.id === id);
        if (idx < 0) return prev;
        const next = [...prev];
        if (dir === 'up' && idx < next.length - 1) {
          [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
        } else if (dir === 'down' && idx > 0) {
          [next[idx], next[idx - 1]] = [next[idx - 1], next[idx]];
        }
        return next;
      });
    },
    []
  );

  const undo = useCallback(() => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      const snap = elementsRef.current;
      setFuture((f) => [snap, ...f.slice(0, MAX_HISTORY - 1)]);
      setElements(prev);
      return h.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (f.length === 0) return f;
      const next = f[0];
      const snap = elementsRef.current;
      setHistory((h) => [...h.slice(-MAX_HISTORY), snap]);
      setElements(next);
      return f.slice(1);
    });
  }, []);

  const selectedElement = elements.find((e) => e.id === selectedId) ?? null;

  // ── Recent Projects ────────────────────────────────────────────
  const saveToRecents = useCallback(async (payload: SavedEditorState, thumbnail: string) => {
    try {
      const sessionId = sessionIdRef.current;
      // Image is already stored separately in IMAGE_KEY_PREFIX + sessionId by handleSave.
      // Strip any inline imageUrl from the project state so we don't double-store large base64 blobs.
      const { imageUrl: _stripped, ...payloadWithoutImage } = payload;
      const stateToStore: SavedEditorState = { ...payloadWithoutImage, imageKey: sessionId };
      localStorage.setItem(`adforge_project_${sessionId}`, JSON.stringify(stateToStore));

      // Update metadata list
      const rawMeta = localStorage.getItem(RECENT_PROJECTS_KEY);
      const metas: RecentProjectMeta[] = rawMeta ? JSON.parse(rawMeta) : [];
      const existingIdx = metas.findIndex((m) => m.id === sessionId);
      const meta: RecentProjectMeta = {
        id: sessionId,
        savedAt: payload.savedAt,
        thumbnail,
        elementCount: payload.elements.length,
        aspectRatio: payload.aspectRatio ?? '1:1',
      };
      if (existingIdx >= 0) {
        metas[existingIdx] = meta;
      } else {
        metas.unshift(meta);
      }
      const trimmed = metas.slice(0, MAX_RECENT_PROJECTS);
      localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(trimmed));
      setRecentProjects(trimmed);
    } catch { /* Ignore storage quota errors */ }
  }, []);

  const loadRecentProject = useCallback((meta: RecentProjectMeta) => {
    try {
      const raw = localStorage.getItem(`adforge_project_${meta.id}`);
      if (!raw) return;
      const saved: SavedEditorState = JSON.parse(raw);
      setElements(saved.elements ?? []);
      setOverlayOpacity(saved.overlayOpacity ?? 0);
      setAnimation(saved.animation ?? 'none');
      setImageFit(saved.imageFit ?? 'cover');
      setImagePositionX(saved.imagePositionX ?? 50);
      setImagePositionY(saved.imagePositionY ?? 50);
      // Load image: try the project-specific key, then inline (old format)
      const imageUrl =
        loadImageFromKey(saved.imageKey, undefined) ||
        loadImageFromKey(MAIN_IMAGE_KEY, saved.imageUrl);
      if (imageUrl) setCurrentImageUrl(imageUrl);
      // Adopt this project's session so saves update it
      sessionIdRef.current = meta.id;
    } catch { /* ignore */ }
  }, []);

  const deleteRecentProject = useCallback((id: string) => {
    try {
      localStorage.removeItem(`adforge_project_${id}`);
      const rawMeta = localStorage.getItem(RECENT_PROJECTS_KEY);
      const metas: RecentProjectMeta[] = rawMeta ? JSON.parse(rawMeta) : [];
      const updated = metas.filter((m) => m.id !== id);
      localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(updated));
      setRecentProjects(updated);
    } catch { /* ignore */ }
  }, []);

  const handleExportWithOverlays = useCallback(async () => {
    if (!currentImageUrl) {
      setExportError('No image loaded. Please upload an image first.');
      return;
    }
    if (exportErrorTimerRef.current) clearTimeout(exportErrorTimerRef.current);
    setExportError(null);
    setIsExporting(true);
    try {
      await exportStaticPng({
        elements,
        backgroundImageUrl: currentImageUrl,
        overlayOpacity,
        animation,
        imageFit,
        imagePositionX,
        imagePositionY,
        aspectRatio,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Export failed. Please try again.';
      setExportError(msg);
      exportErrorTimerRef.current = setTimeout(() => setExportError(null), 8000);
    } finally {
      setIsExporting(false);
    }
  }, [currentImageUrl, elements, overlayOpacity, animation, imageFit, imagePositionX, imagePositionY, aspectRatio]);

  // Save editor state to localStorage
  const handleSave = useCallback(() => {
    setIsSaving(true);
    const sessionId = sessionIdRef.current;

    // 1. Save image to its own key (avoids base64 bloating the main state object)
    if (currentImageUrl) {
      saveImageSeparately(IMAGE_KEY_PREFIX + sessionId, currentImageUrl);
      // Also keep the shared "main" key so the restore-on-mount path works
      saveImageSeparately(IMAGE_KEY_PREFIX + MAIN_IMAGE_KEY, currentImageUrl);
    }

    // 2. Save state WITHOUT the image URL inlined
    const payload: SavedEditorState = {
      elements,
      overlayOpacity,
      animation,
      imageFit,
      imagePositionX,
      imagePositionY,
      savedAt: Date.now(),
      imageKey: sessionId,
      aspectRatio,
    };
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(payload));
      setSaveLabel('Saved!');
      setTimeout(() => setSaveLabel('Save'), 2500);
    } catch {
      setSaveLabel('Failed');
      setTimeout(() => setSaveLabel('Save'), 2500);
    } finally {
      setIsSaving(false);
    }
    // Also persist to recents (async, non-blocking)
    if (currentImageUrl) {
      saveToRecents(payload, currentThumbnail);
    }
  }, [elements, overlayOpacity, animation, imageFit, imagePositionX, imagePositionY, currentImageUrl, aspectRatio, currentThumbnail, saveToRecents]);

  // Restore saved state on mount (only if arriving fresh without location state elements)
  useEffect(() => {
    if (!state?.initialHeadline && !state?.initialSubheadline && !state?.initialCta) {
      try {
        const raw = localStorage.getItem(SAVE_KEY);
        if (!raw) return;
        const saved: SavedEditorState = JSON.parse(raw);
        // Restore if saved within last 30 days
        if (Date.now() - saved.savedAt < 30 * 86_400_000) {
          setElements(saved.elements ?? []);
          setOverlayOpacity(saved.overlayOpacity ?? 0);
          setAnimation(saved.animation ?? 'none');
          setImageFit(saved.imageFit ?? 'cover');
          setImagePositionX(saved.imagePositionX ?? 50);
          setImagePositionY(saved.imagePositionY ?? 50);
          if (!state?.imageUrl) {
            // Try saved.imageKey first, then the shared MAIN_IMAGE_KEY, then inline imageUrl (old format)
            const restoredImage =
              loadImageFromKey(saved.imageKey, undefined) ||
              loadImageFromKey(MAIN_IMAGE_KEY, saved.imageUrl);
            if (restoredImage) setCurrentImageUrl(restoredImage);
          }
          // Adopt the session ID from the saved state so saves update the existing recent entry
          if (saved.imageKey) {
            sessionIdRef.current = saved.imageKey;
          } else {
            sessionIdRef.current = 'migrated_legacy_save';
          }
        }
      } catch {
        // Ignore corrupt saves
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup export error timer on unmount
  useEffect(() => {
    return () => {
      if (exportErrorTimerRef.current) clearTimeout(exportErrorTimerRef.current);
    };
  }, []);

  // Keyboard shortcuts: Ctrl+S · Ctrl+Z · Ctrl+Y / Ctrl+Shift+Z
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      const key = e.key.toLowerCase();
      if (key === 's') {
        e.preventDefault();
        handleSave();
      } else if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
        e.preventDefault();
        redo();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, undo, redo]);

  // Auto-save every 30 seconds while editing
  useEffect(() => {
    const interval = setInterval(() => {
      if (!currentImageUrl) return;
      const sessionId = sessionIdRef.current;
      // Save image to its own key (safe for large base64 data URLs)
      saveImageSeparately(IMAGE_KEY_PREFIX + sessionId, currentImageUrl);
      saveImageSeparately(IMAGE_KEY_PREFIX + MAIN_IMAGE_KEY, currentImageUrl);
      const payload: SavedEditorState = {
        elements,
        overlayOpacity,
        animation,
        imageFit,
        imagePositionX,
        imagePositionY,
        savedAt: Date.now(),
        imageKey: sessionId,
        aspectRatio,
      };
      try {
        localStorage.setItem(SAVE_KEY, JSON.stringify(payload));
      } catch { /* ignore */ }
      saveToRecents(payload, currentThumbnail);
    }, 30_000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, overlayOpacity, animation, imageFit, imagePositionX, imagePositionY, currentImageUrl, currentThumbnail]);

  // Generate HTML embed code from current editor state
  const generateHtmlEmbed = useCallback((): string => {
    const ratioCss: Record<AspectRatio, string> = {
      '1:1': '1/1',
      '9:16': '9/16',
      '16:9': '16/9',
    };
    const aspect = ratioCss[aspectRatio] ?? '1/1';

    // Build CSS animations based on selected animation
    let animCss = '';
    let imgAnimStyle = '';
    let ctaAnimStyle = '';
    let textAnimStyle = '';

    switch (animation) {
      case 'ken-burns':
        animCss = `
    @keyframes ad-ken-burns {
      0% { transform: scale(1) translate(0,0); }
      33% { transform: scale(1.09) translate(-2%,-1%); }
      66% { transform: scale(1.06) translate(1.5%,-0.5%); }
      100% { transform: scale(1) translate(0,0); }
    }`;
        imgAnimStyle = 'animation: ad-ken-burns 9s ease-in-out infinite;';
        break;
      case 'cta-pulse':
        animCss = `
    @keyframes ad-cta-ring {
      0% { box-shadow: 0 0 0 0 rgba(249,115,22,.75); }
      70% { box-shadow: 0 0 0 14px rgba(249,115,22,0); }
      100% { box-shadow: 0 0 0 0 rgba(249,115,22,0); }
    }`;
        ctaAnimStyle = 'animation: ad-cta-ring 1.6s ease-out infinite;';
        break;
      case 'text-reveal':
        animCss = `
    @keyframes ad-text-reveal {
      0% { opacity: 0; transform: translateY(14px); }
      18% { opacity: 1; transform: translateY(0); }
      78% { opacity: 1; transform: translateY(0); }
      100% { opacity: 0; transform: translateY(-10px); }
    }`;
        textAnimStyle = 'animation: ad-text-reveal 4s ease-in-out infinite;';
        break;
      case 'spotlight':
        animCss = `
    @keyframes ad-spotlight {
      0% { transform: translateX(-160%) skewX(-20deg); opacity: 0; }
      8% { opacity: 0.55; }
      92% { opacity: 0.55; }
      100% { transform: translateX(260%) skewX(-20deg); opacity: 0; }
    }`;
        break;
      default:
        break;
    }

    // Build overlay gradient based on opacity
    const overlayColor = `rgba(0,0,0,${overlayOpacity})`;

    // Build element HTML
    const elementsHtml = elements
      .filter((el) => el.visible)
      .map((el) => {
        const isCta = el.type === 'button';
        const isText = el.type === 'text';
        const baseStyles: Record<string, string> = {
          position: 'absolute',
          left: `${el.x}%`,
          top: `${el.y}%`,
          fontFamily: el.style.fontFamily,
          fontSize: `${el.style.fontSize}px`,
          fontWeight: String(el.style.fontWeight),
          color: el.style.color,
          backgroundColor: el.style.backgroundColor === 'transparent' ? 'transparent' : el.style.backgroundColor,
          borderRadius: `${el.style.borderRadius}px`,
          padding: `${el.style.paddingY}px ${el.style.paddingX}px`,
          opacity: String(el.style.opacity),
          letterSpacing: `${el.style.letterSpacing}px`,
          lineHeight: el.style.lineHeight,
          textAlign: el.style.textAlign,
          textTransform: el.style.textTransform,
          border: el.style.border === 'none' ? 'none' : el.style.border,
          textShadow: el.style.textShadow ? '0 2px 8px rgba(0,0,0,0.7)' : 'none',
          maxWidth: el.style.width ? `${el.style.width}%` : 'auto',
          width: el.type === 'button' || el.type === 'shape' ? (el.style.width ? `${el.style.width}%` : 'auto') : 'auto',
          zIndex: '10',
        };

        if (isCta && ctaAnimStyle) {
          baseStyles.animation = 'ad-cta-ring 1.6s ease-out infinite';
        }
        if (isText && textAnimStyle) {
          baseStyles.animation = 'ad-text-reveal 4s ease-in-out infinite';
        }

        const styleString = Object.entries(baseStyles)
          .map(([k, v]) => `${k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}:${v}`)
          .join(';');

        if (isCta) {
          return `    <a href="#" style="${styleString};text-decoration:none;display:inline-block;${ctaAnimStyle}">${el.content}</a>`;
        }
        return `    <div style="${styleString}">${el.content}</div>`;
      })
      .join('\n');

    // Spotlight overlay HTML if needed
    const spotlightHtml = animation === 'spotlight' ? `
    <div style="position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:5">
      <div style="position:absolute;top:0;left:0;width:40%;height:100%;background:linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.35) 50%,transparent 70%);animation:ad-spotlight 3.2s ease-in-out infinite"></div>
    </div>` : '';

    return `<!-- Ad Creative — Generated by AdForge -->
<div style="position:relative;width:100%;max-width:600px;aspect-ratio:${aspect};overflow:hidden;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;border-radius:16px;">
  <style>${animCss}
  </style>
  <img
    src="${currentImageUrl}"
    alt="Ad Creative"
    style="position:absolute;inset:0;width:100%;height:100%;object-fit:${imageFit};object-position:${imagePositionX}% ${imagePositionY}%;${imgAnimStyle}"
  />
  <div style="position:absolute;inset:0;background-color:${overlayColor};z-index:1"></div>
${spotlightHtml}
${elementsHtml}
</div>`;
  }, [elements, overlayOpacity, animation, imageFit, imagePositionX, imagePositionY, aspectRatio, currentImageUrl]);

  const handleExportHtml = useCallback(() => {
    const html = generateHtmlEmbed();
    navigator.clipboard.writeText(html);
    setHtmlCopied(true);
    setTimeout(() => setHtmlCopied(false), 2500);
  }, [generateHtmlEmbed]);

  const handleExportVideo = useCallback(async () => {
    if (!currentImageUrl || isExportingVideo) return;
    setIsExportingVideo(true);
    setVideoProgress(0);
    try {
      await exportAnimatedVideo(
        {
          elements,
          backgroundImageUrl: currentImageUrl,
          overlayOpacity,
          animation,
          imageFit,
          imagePositionX,
          imagePositionY,
          aspectRatio,
        },
        (p) => setVideoProgress(p),
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Video export failed';
      // Re-use the export error display
      setExportError(msg);
      if (exportErrorTimerRef.current) clearTimeout(exportErrorTimerRef.current);
      exportErrorTimerRef.current = setTimeout(() => setExportError(null), 8000);
    } finally {
      setIsExportingVideo(false);
      setVideoProgress(0);
    }
  }, [currentImageUrl, isExportingVideo, elements, overlayOpacity, animation, imageFit, imagePositionX, imagePositionY, aspectRatio]);

  // ── Start New ──────────────────────────────────────────────────
  const handleStartNew = useCallback(() => {
    setCurrentImageUrl(null);
    setElements([]);
    setSelectedId(null);
    setOverlayOpacity(0.45);
    setAnimation('none');
    setImageFit('cover');
    setImagePositionX(50);
    setImagePositionY(50);
    setHistory([]);
    setFuture([]);
    sessionIdRef.current = `session_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    try {
      localStorage.removeItem(SAVE_KEY);
    } catch { /* ignore */ }
  }, []);

  // ── Upload screen ──────────────────────────────────────────────
  if (!currentImageUrl) {
    const formatDate = (ts: number) => {
      const d = new Date(ts);
      const now = new Date();
      const diffMs = now.getTime() - d.getTime();
      const diffMins = Math.floor(diffMs / 60_000);
      const diffHours = Math.floor(diffMs / 3_600_000);
      const diffDays = Math.floor(diffMs / 86_400_000);
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const ratioLabel: Record<string, string> = { '1:1': 'Square', '16:9': 'Landscape', '9:16': 'Portrait' };

    return (
      <div className="fixed inset-0 z-50 bg-stone-950 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="h-14 bg-stone-900 border-b border-stone-800 flex items-center px-4 gap-3 shrink-0">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-stone-400 hover:text-stone-200 hover:bg-stone-800 transition-colors cursor-pointer text-sm font-medium whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            Back
          </button>
          <div className="w-px h-6 bg-stone-800" />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-amber-400">
              <i className="ri-edit-2-fill text-white text-xs" />
            </div>
            <span className="text-sm font-bold text-stone-200 whitespace-nowrap">Ad Editor</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col items-center px-6 py-10 gap-10 min-h-full">

            {/* Upload zone */}
            <div className="w-full max-w-xl">
              <div className="text-center mb-7">
                <h1 className="text-2xl font-bold text-white mb-2">Upload Your Image</h1>
                <p className="text-stone-400 text-sm">
                  Choose an image to use as the background for your ad. Add text, buttons, and shapes on top.
                </p>
              </div>

              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-2xl p-8 md:p-14 flex flex-col items-center justify-center gap-4 cursor-pointer transition-all duration-200 ${
                  isDraggingOver
                    ? 'border-orange-400 bg-orange-500/10'
                    : 'border-stone-700 hover:border-orange-500/60 hover:bg-stone-900/60 bg-stone-900/30'
                }`}
              >
                <div className={`w-16 h-16 flex items-center justify-center rounded-2xl transition-colors ${
                  isDraggingOver ? 'bg-orange-500/20' : 'bg-stone-800'
                }`}>
                  <i className={`ri-image-add-line text-3xl ${isDraggingOver ? 'text-orange-400' : 'text-stone-500'}`} />
                </div>
                <div className="text-center">
                  <p className="text-white font-semibold mb-1">
                    {isDraggingOver ? 'Drop it here!' : 'Drag & drop your image'}
                  </p>
                  <p className="text-stone-500 text-sm">or click to browse files</p>
                </div>
                <div className="flex items-center gap-2 text-xs text-stone-600">
                  <i className="ri-file-image-line" />
                  <span>JPG, PNG, WEBP, GIF — up to 50 MB</span>
                </div>
                {isDraggingOver && (
                  <div className="absolute inset-0 rounded-2xl bg-orange-500/5 pointer-events-none" />
                )}
              </div>

              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileInput} />

              <div className="flex items-center gap-3 mt-5">
                <div className="flex-1 h-px bg-stone-800" />
                <span className="text-xs text-stone-600">or start from a generated ad</span>
                <div className="flex-1 h-px bg-stone-800" />
              </div>
              <div className="mt-4 flex justify-center">
                <button
                  onClick={() => navigate('/dashboard/generate')}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-stone-800 border border-stone-700 hover:border-orange-500/40 hover:bg-stone-700 text-stone-300 hover:text-white text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-flashlight-fill text-orange-400" />
                  Go to Ad Generator
                </button>
              </div>
            </div>

            {/* Recent Projects */}
            {recentProjects.length > 0 && (
              <div className="w-full max-w-3xl">
                {/* Section header */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-stone-800">
                    <i className="ri-time-line text-orange-400 text-sm" />
                  </div>
                  <h2 className="text-base font-bold text-white">Recent Projects</h2>
                  <span className="text-xs text-stone-600 bg-stone-800 px-2 py-0.5 rounded-full">{recentProjects.length}</span>
                  <span className="flex-1" />
                  <span className="text-xs text-stone-600">Click to resume editing</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {recentProjects.map((project) => (
                    <div
                      key={project.id}
                      className="group relative bg-stone-900 border border-stone-800 rounded-2xl overflow-hidden hover:border-orange-500/50 hover:bg-stone-800/80 transition-all duration-200 cursor-pointer"
                      onClick={() => loadRecentProject(project)}
                    >
                      {/* Thumbnail */}
                      <div className="relative w-full h-40 bg-stone-800 overflow-hidden">
                        {project.thumbnail ? (
                          <img
                            src={project.thumbnail}
                            alt="Project thumbnail"
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <i className="ri-image-2-line text-3xl text-stone-700" />
                          </div>
                        )}
                        {/* Overlay on hover */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-2 bg-orange-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg whitespace-nowrap">
                            <i className="ri-edit-2-fill" />
                            Resume Editing
                          </div>
                        </div>
                        {/* Aspect ratio badge */}
                        <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full font-medium">
                          {ratioLabel[project.aspectRatio] ?? project.aspectRatio}
                        </div>
                      </div>

                      {/* Meta */}
                      <div className="p-3 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-stone-300 text-xs font-semibold truncate">
                            {formatDate(project.savedAt)}
                          </p>
                          <p className="text-stone-600 text-xs mt-0.5">
                            {project.elementCount} element{project.elementCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteRecentProject(project.id); }}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-stone-600 hover:text-red-400 hover:bg-stone-700 transition-colors cursor-pointer opacity-0 group-hover:opacity-100 shrink-0"
                          title="Delete this project"
                        >
                          <i className="ri-delete-bin-line text-xs" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="text-center text-xs text-stone-700 mt-5">
                  Projects are saved locally in your browser — clearing browser data will remove them.
                </p>
              </div>
            )}

          </div>
        </div>
      </div>
    );
  }

  // Auto-switch to properties tab when an element is selected on mobile
  const handleMobileSelectElement = (id: string | null) => {
    setSelectedId(id);
    if (id) {
      setMobilePanelTab('properties');
      setMobilePanelOpen(true);
    }
  };

  // ── Editor ─────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 bg-stone-950 flex flex-col overflow-hidden">
      {eraseOpen && currentImageUrl && (
        <EraseModal
          imageUrl={currentImageUrl}
          onSave={(newUrl) => { setCurrentImageUrl(newUrl); setEraseOpen(false); }}
          onClose={() => setEraseOpen(false)}
        />
      )}

      <EditorToolbar
        onBack={() => navigate(-1)}
        onStartNew={handleStartNew}
        onAddText={addText}
        onAddButton={addButton}
        onAddShape={addShape}
        onAddLogo={addLogo}
        onUndo={undo}
        onRedo={redo}
        canUndo={history.length > 0}
        canRedo={future.length > 0}
        animation={animation}
        onAnimationChange={setAnimation}
        imageUrl={currentImageUrl}
        onImageChange={handleImageChange}
        onExportWithOverlays={handleExportWithOverlays}
        isExporting={isExporting}
        exportError={exportError}
        onSave={handleSave}
        isSaving={isSaving}
        saveLabel={saveLabel}
        onExportHtml={handleExportHtml}
        htmlCopied={htmlCopied}
        onExportVideo={handleExportVideo}
        isExportingVideo={isExportingVideo}
        videoProgress={videoProgress}
        onErase={() => setEraseOpen(true)}
      />

      {/* ── Desktop layout (md+): side panels ─────────────────── */}
      <div className="hidden md:flex flex-1 overflow-hidden">
        <LayersPanel
          elements={elements}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onDelete={deleteElement}
          onDuplicate={duplicateElement}
          onToggleVisible={(id) =>
            updateElement(id, { visible: !elements.find((e) => e.id === id)?.visible })
          }
          onToggleLock={(id) =>
            updateElement(id, { locked: !elements.find((e) => e.id === id)?.locked })
          }
          onReorder={reorderElement}
        />

        <div className="flex-1 flex items-center justify-center bg-stone-900 overflow-auto p-6">
          <EditorCanvas
            imageUrl={currentImageUrl}
            aspectRatio={aspectRatio}
            elements={elements}
            selectedId={selectedId}
            overlayOpacity={overlayOpacity}
            animation={animation}
            imageFit={imageFit}
            imagePositionX={imagePositionX}
            imagePositionY={imagePositionY}
            onSelectElement={setSelectedId}
            onUpdatePosition={updatePosition}
            onUpdateElement={updateElementSilent}
            onInteractionStart={pushHistoryNow}
            onImagePositionChange={(x, y) => { setImagePositionX(x); setImagePositionY(y); }}
            containerRef={canvasContainerRef}
            isExporting={isExporting}
          />
        </div>

        <PropertiesPanel
          selectedElement={selectedElement}
          overlayOpacity={overlayOpacity}
          onOverlayOpacityChange={setOverlayOpacity}
          imageFit={imageFit}
          onImageFitChange={setImageFit}
          imagePositionX={imagePositionX}
          imagePositionY={imagePositionY}
          onImagePositionXChange={setImagePositionX}
          onImagePositionYChange={setImagePositionY}
          onUpdateElement={(updates) => {
            if (selectedId) updateElement(selectedId, updates);
          }}
          onDelete={() => selectedId && deleteElement(selectedId)}
          onDuplicate={() => selectedId && duplicateElement(selectedId)}
          onReplaceImage={(file) => { if (selectedId) replaceImageElement(selectedId, file); }}
        />
      </div>

      {/* ── Mobile layout (<md): canvas + bottom sheet ─────────── */}
      <div className="flex md:hidden flex-col flex-1 overflow-hidden">
        {/* Canvas area */}
        <div className="flex-1 flex items-center justify-center bg-stone-900 overflow-auto p-3 min-h-0">
          <EditorCanvas
            imageUrl={currentImageUrl}
            aspectRatio={aspectRatio}
            elements={elements}
            selectedId={selectedId}
            overlayOpacity={overlayOpacity}
            animation={animation}
            imageFit={imageFit}
            imagePositionX={imagePositionX}
            imagePositionY={imagePositionY}
            onSelectElement={handleMobileSelectElement}
            onUpdatePosition={updatePosition}
            onUpdateElement={updateElementSilent}
            onInteractionStart={pushHistoryNow}
            onImagePositionChange={(x, y) => { setImagePositionX(x); setImagePositionY(y); }}
            containerRef={canvasContainerRef}
            isExporting={isExporting}
          />
        </div>

        {/* Bottom panel toggle bar */}
        <div className="shrink-0 bg-stone-900 border-t border-stone-800">
          {/* Tab bar */}
          <div className="flex items-center px-3 pt-2 gap-2">
            <button
              onClick={() => { setMobilePanelTab('layers'); setMobilePanelOpen((o) => mobilePanelTab !== 'layers' ? true : !o); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${mobilePanelTab === 'layers' && mobilePanelOpen ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30' : 'bg-stone-800 text-stone-400 border border-stone-700'}`}
            >
              <i className="ri-stack-line" />
              Layers
              <span className="bg-stone-700 text-stone-400 text-xs px-1.5 py-0.5 rounded-full leading-none">{elements.length}</span>
            </button>
            <button
              onClick={() => { setMobilePanelTab('properties'); setMobilePanelOpen((o) => mobilePanelTab !== 'properties' ? true : !o); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${mobilePanelTab === 'properties' && mobilePanelOpen ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30' : 'bg-stone-800 text-stone-400 border border-stone-700'}`}
            >
              <i className="ri-settings-3-line" />
              {selectedElement ? 'Properties' : 'Canvas'}
            </button>
            <div className="flex-1" />
            {mobilePanelOpen && (
              <button
                onClick={() => setMobilePanelOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-lg bg-stone-800 text-stone-500 hover:text-stone-300 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-down-s-line text-base" />
              </button>
            )}
          </div>

          {/* Expandable panel content */}
          {mobilePanelOpen && (
            <div className="h-64 overflow-y-auto border-t border-stone-800 mt-2">
              {mobilePanelTab === 'layers' ? (
                <LayersPanel
                  elements={elements}
                  selectedId={selectedId}
                  onSelect={(id) => { setSelectedId(id); setMobilePanelTab('properties'); }}
                  onDelete={deleteElement}
                  onDuplicate={duplicateElement}
                  onToggleVisible={(id) =>
                    updateElement(id, { visible: !elements.find((e) => e.id === id)?.visible })
                  }
                  onToggleLock={(id) =>
                    updateElement(id, { locked: !elements.find((e) => e.id === id)?.locked })
                  }
                  onReorder={reorderElement}
                />
              ) : (
                <PropertiesPanel
                  selectedElement={selectedElement}
                  overlayOpacity={overlayOpacity}
                  onOverlayOpacityChange={setOverlayOpacity}
                  imageFit={imageFit}
                  onImageFitChange={setImageFit}
                  imagePositionX={imagePositionX}
                  imagePositionY={imagePositionY}
                  onImagePositionXChange={setImagePositionX}
                  onImagePositionYChange={setImagePositionY}
                  onUpdateElement={(updates) => {
                    if (selectedId) updateElement(selectedId, updates);
                  }}
                  onDelete={() => { if (selectedId) { deleteElement(selectedId); setMobilePanelOpen(false); } }}
                  onDuplicate={() => selectedId && duplicateElement(selectedId)}
                  onReplaceImage={(file) => { if (selectedId) replaceImageElement(selectedId, file); }}
                />
              )}
            </div>
          )}

          {/* Safe area spacer */}
          <div className="h-2" />
        </div>
      </div>
    </div>
  );
}
